import { useState, useEffect, useMemo } from 'react';
import { Button, Input, Select, toast } from '../../components/ui';
import { Autocomplete } from '../../components/ui/Autocomplete';
import { api, ApiClientError } from '../../services/api-client';
import CodiceFiscale from 'codice-fiscale-js';
// @ts-ignore
import { COMUNI } from 'codice-fiscale-js/src/lista-comuni';

// Build autocomplete options from COMUNI list: [codice_catastale, provincia, nome, cessato]
const COMUNI_OPTIONS = (COMUNI as any[])
  .filter((c) => c[3] === 1) // Only active municipalities (1 = active)
  .map((c) => ({
    value: c[2], // nome comune
    label: c[2], // display name
    extra: c[1], // provincia
  }));

interface CensusFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  cityOfBirth: string;
  provinceOfBirth: string;
  gender: string;
  codiceFiscale: string;
  personalEmail: string;
  nationality: string;
  phoneNumber: string;
  mobileNumber: string;
  iban: string;
  residenceAddress: string;
  residenceCity: string;
  residenceProvince: string;
  residenceZip: string;
  residenceCountry: string;
  domicileAddress: string;
  domicileCity: string;
  domicileProvince: string;
  domicileZip: string;
  domicileCountry: string;
  portalCode: string;
  roleCode: string;
}

const EMPTY_FORM: CensusFormData = {
  firstName: '', lastName: '', dateOfBirth: '', cityOfBirth: '', provinceOfBirth: '',
  gender: '', codiceFiscale: '', personalEmail: '',
  nationality: '', phoneNumber: '', mobileNumber: '', iban: '',
  residenceAddress: '', residenceCity: '', residenceProvince: '', residenceZip: '', residenceCountry: '',
  domicileAddress: '', domicileCity: '', domicileProvince: '', domicileZip: '', domicileCountry: '',
  portalCode: '', roleCode: '',
};

const GENDER_OPTIONS = [
  { value: 'M', label: 'Maschio' },
  { value: 'F', label: 'Femmina' },
];

const PORTAL_OPTIONS = [
  { value: 'documentale', label: 'Documentale Dipendenti' },
  { value: 'fatturazione', label: 'Fatturazione Online' },
];

const ROLE_OPTIONS = [
  { value: 'user', label: 'Utente' },
  { value: 'admin', label: 'Amministratore' },
  { value: 'operator', label: 'Operatore' },
];

/**
 * Census page — Ufficio Personale creates a new user.
 * Auto-calculates Codice Fiscale from personal data.
 */
export function CensusPage() {
  const [form, setForm] = useState<CensusFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [cfAutoCalculated, setCfAutoCalculated] = useState(false);

  const updateField = (field: keyof CensusFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  // Auto-calculate CF when all required fields are filled
  useEffect(() => {
    if (form.firstName && form.lastName && form.dateOfBirth && form.cityOfBirth && form.provinceOfBirth && form.gender) {
      try {
        const [year, month, day] = form.dateOfBirth.split('-').map(Number);
        const cf = new CodiceFiscale({
          name: form.firstName,
          surname: form.lastName,
          gender: form.gender as 'M' | 'F',
          day,
          month,
          year,
          birthplace: form.cityOfBirth,
          birthplaceProvincia: form.provinceOfBirth,
        });
        updateField('codiceFiscale', cf.toString());
        setCfAutoCalculated(true);
      } catch {
        // City not found or invalid data — let user input manually
        setCfAutoCalculated(false);
      }
    }
  }, [form.firstName, form.lastName, form.dateOfBirth, form.cityOfBirth, form.provinceOfBirth, form.gender]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.firstName.trim()) newErrors.firstName = 'Campo obbligatorio';
    if (!form.lastName.trim()) newErrors.lastName = 'Campo obbligatorio';
    if (!form.dateOfBirth) newErrors.dateOfBirth = 'Campo obbligatorio';
    if (!form.cityOfBirth.trim()) newErrors.cityOfBirth = 'Campo obbligatorio';
    if (!form.gender) newErrors.gender = 'Campo obbligatorio';
    if (!form.codiceFiscale.trim()) {
      newErrors.codiceFiscale = 'Campo obbligatorio';
    } else if (form.codiceFiscale.length !== 16) {
      newErrors.codiceFiscale = 'Il Codice Fiscale deve essere di 16 caratteri';
    }
    if (!form.personalEmail.trim()) {
      newErrors.personalEmail = 'Campo obbligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.personalEmail)) {
      newErrors.personalEmail = 'Formato email non valido';
    }
    if (!form.portalCode) newErrors.portalCode = 'Selezionare almeno un portale';
    if (!form.roleCode) newErrors.roleCode = 'Selezionare un ruolo';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post('/admin/users/census', {
        ...form,
        portalAssignments: [{ portalCode: form.portalCode, roleCode: form.roleCode }],
      });
      toast.success('Utente creato con successo. Email di registrazione inviata.');
      setForm(EMPTY_FORM);
      setCfAutoCalculated(false);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message, {
          label: 'Riprova',
          onClick: () => handleSubmit(new Event('submit') as any),
        });
      } else {
        toast.error('Errore imprevisto. Riprovare più tardi.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="census-page">
      <h2>Censimento Nuovo Utente</h2>
      <p>Inserisci i dati del dipendente per creare il suo account.</p>

      <form onSubmit={handleSubmit} aria-label="Form censimento utente" noValidate>
        {/* Dati obbligatori */}
        <fieldset>
          <legend>Dati personali (obbligatori)</legend>
          <div className="form-row">
            <Input label="Nome" value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} error={errors.firstName} required />
            <Input label="Cognome" value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} error={errors.lastName} required />
          </div>
          <div className="form-row">
            <Input label="Data di nascita" type="date" value={form.dateOfBirth} onChange={(e) => updateField('dateOfBirth', e.target.value)} error={errors.dateOfBirth} required />
            <Autocomplete
              label="Comune di nascita"
              value={form.cityOfBirth}
              onChange={(val) => updateField('cityOfBirth', val)}
              onSelect={(option) => {
                updateField('cityOfBirth', option.value);
                updateField('provinceOfBirth', option.extra || '');
              }}
              options={COMUNI_OPTIONS}
              error={errors.cityOfBirth}
              required
              placeholder="Digita il nome del comune..."
              helpText={form.provinceOfBirth ? `Provincia: ${form.provinceOfBirth}` : 'Digita almeno 2 caratteri per i suggerimenti'}
            />
          </div>
          <div className="form-row">
            <Select label="Sesso" options={GENDER_OPTIONS} value={form.gender} onChange={(e) => updateField('gender', e.target.value)} error={errors.gender} placeholder="Seleziona..." />
            <Input
              label="Codice Fiscale"
              value={form.codiceFiscale}
              onChange={(e) => { updateField('codiceFiscale', e.target.value.toUpperCase()); setCfAutoCalculated(false); }}
              error={errors.codiceFiscale}
              maxLength={16}
              required
              helpText={cfAutoCalculated ? '✓ Calcolato automaticamente' : 'Inserisci manualmente o compila i campi sopra per il calcolo automatico'}
            />
          </div>
          <Input label="Email personale" type="email" value={form.personalEmail} onChange={(e) => updateField('personalEmail', e.target.value)} error={errors.personalEmail} required helpText="A questo indirizzo verranno inviate le istruzioni per la registrazione" />
        </fieldset>

        {/* Portale e ruolo */}
        <fieldset>
          <legend>Assegnazione portale</legend>
          <div className="form-row">
            <Select label="Portale" options={PORTAL_OPTIONS} value={form.portalCode} onChange={(e) => updateField('portalCode', e.target.value)} error={errors.portalCode} placeholder="Seleziona portale..." />
            <Select label="Ruolo" options={ROLE_OPTIONS} value={form.roleCode} onChange={(e) => updateField('roleCode', e.target.value)} error={errors.roleCode} placeholder="Seleziona ruolo..." />
          </div>
        </fieldset>

        {/* Dati opzionali */}
        <fieldset>
          <legend>Dati aggiuntivi (opzionali)</legend>
          <div className="form-row">
            <Input label="Nazionalità" value={form.nationality} onChange={(e) => updateField('nationality', e.target.value)} />
            <Input label="Telefono" type="tel" value={form.phoneNumber} onChange={(e) => updateField('phoneNumber', e.target.value)} />
            <Input label="Cellulare" type="tel" value={form.mobileNumber} onChange={(e) => updateField('mobileNumber', e.target.value)} />
          </div>
          <Input label="IBAN" value={form.iban} onChange={(e) => updateField('iban', e.target.value)} maxLength={34} />
        </fieldset>

        <fieldset>
          <legend>Residenza (opzionale)</legend>
          <Input label="Indirizzo" value={form.residenceAddress} onChange={(e) => updateField('residenceAddress', e.target.value)} />
          <div className="form-row">
            <Input label="Città" value={form.residenceCity} onChange={(e) => updateField('residenceCity', e.target.value)} />
            <Input label="Provincia" value={form.residenceProvince} onChange={(e) => updateField('residenceProvince', e.target.value)} maxLength={2} />
            <Input label="CAP" value={form.residenceZip} onChange={(e) => updateField('residenceZip', e.target.value)} maxLength={5} />
          </div>
        </fieldset>

        <div style={{ marginTop: 'var(--space-xl)' }}>
          <Button type="submit" loading={loading} fullWidth>
            Crea Utente
          </Button>
        </div>
      </form>
    </div>
  );
}
